/**
 * Sidebar: folder tree plus the Notes / Trash / Vault sections.
 *
 * Styled from the ported Mac tokens — the panda-green gradient runs
 * bottom-up (see `sidebarGradient` for why the stops look inverted).
 */
import {
  ChevronRight,
  FileText,
  Folder,
  Lock,
  LogOut,
  RefreshCw,
  Settings,
  Trash2,
  X,
} from 'lucide-react';
import { sidebarGradient } from '../design/tokens.js';
import { useOverlayScrollbar } from '../lib/ui/overlayScroll.js';
import type { FolderNode } from '../lib/notes/store.js';
import type { SelectionKind } from '../lib/notes/store.js';

export interface SidebarCounts {
  notes: number;
  trash: number;
  vault: number;
}

interface SidebarProps {
  tree: FolderNode[];
  counts: SidebarCounts;
  /** While locked, the vault count stays hidden — it appears after unlock. */
  vaultUnlocked: boolean;
  selection: { kind: SelectionKind; folderId: string | null };
  collapsed: Set<string>;
  onToggleCollapse: (id: string) => void;
  onSelectKind: (kind: SelectionKind) => void;
  onSelectFolder: (id: string) => void;
  onNewFolder: () => void;
  onSync: () => void;
  onOpenSettings: () => void;
  onSignOut: () => void;
  /** Mobile only: closes the full-screen drawer. Omitted on desktop. */
  onClose?: () => void;
  syncing: boolean;
  /** Footer status line: the sync phase while running, else "Last sync …". */
  statusLabel: string | null;
}

function FolderRow({
  node,
  depth,
  selectedId,
  collapsed,
  onToggleCollapse,
  onSelectFolder,
}: {
  node: FolderNode;
  depth: number;
  selectedId: string | null;
  collapsed: Set<string>;
  onToggleCollapse: (id: string) => void;
  onSelectFolder: (id: string) => void;
}) {
  const hasChildren = node.children.length > 0;
  const isCollapsed = collapsed.has(node.folder.id);
  const isSelected = selectedId === node.folder.id;

  return (
    <>
      <div
        className="sidebar-row"
        style={{
          paddingLeft: 10 + depth * 14,
          background: isSelected ? 'var(--sidebar-active)' : undefined,
        }}
        onClick={() => onSelectFolder(node.folder.id)}
        role="treeitem"
        aria-selected={isSelected}
        aria-expanded={hasChildren ? !isCollapsed : undefined}
        tabIndex={0}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onSelectFolder(node.folder.id);
          }
        }}
      >
        <span
          className="sidebar-disclosure"
          onClick={(event) => {
            if (!hasChildren) return;
            event.stopPropagation();
            onToggleCollapse(node.folder.id);
          }}
          aria-hidden={!hasChildren}
          style={{ visibility: hasChildren ? 'visible' : 'hidden' }}
        >
          <ChevronRight
            size={12}
            strokeWidth={2.25}
            style={{
              transition: 'transform 0.15s ease',
              transform: isCollapsed ? 'rotate(0deg)' : 'rotate(90deg)',
            }}
          />
        </span>
        <Folder size={14} strokeWidth={1.75} style={{ flexShrink: 0 }} />
        <span className="sidebar-row-title">{node.folder.name}</span>
      </div>

      {hasChildren &&
        !isCollapsed &&
        node.children.map((child) => (
          <FolderRow
            key={child.folder.id}
            node={child}
            depth={depth + 1}
            selectedId={selectedId}
            collapsed={collapsed}
            onToggleCollapse={onToggleCollapse}
            onSelectFolder={onSelectFolder}
          />
        ))}
    </>
  );
}

const SECTION_ICONS = {
  notes: FileText,
  trash: Trash2,
  vault: Lock,
} as const;

export function Sidebar({
  tree,
  counts,
  vaultUnlocked,
  selection,
  collapsed,
  onToggleCollapse,
  onSelectKind,
  onSelectFolder,
  onNewFolder,
  onSync,
  onOpenSettings,
  onSignOut,
  onClose,
  syncing,
  statusLabel,
}: SidebarProps) {
  // Floating capsule scrollbar (macOS overlay parity) — see lib/ui/overlayScroll.ts.
  const folderScroll = useOverlayScrollbar<HTMLDivElement>();
  const sections: Array<{ kind: SelectionKind; label: string; count: number }> = [
    { kind: 'notes', label: 'Notes', count: counts.notes },
    { kind: 'trash', label: 'Trash', count: counts.trash },
    { kind: 'vault', label: 'Vault', count: counts.vault },
  ];

  return (
    <aside className="sidebar" style={{ background: sidebarGradient }}>
      <div className="sidebar-section">
        {sections.map(({ kind, label, count }) => {
          const Icon = SECTION_ICONS[kind];
          const isActive = selection.kind === kind && selection.folderId === null;
          return (
            <div
              key={kind}
              className="sidebar-row"
              style={{ background: isActive ? 'var(--sidebar-active)' : undefined }}
              onClick={() => onSelectKind(kind)}
              role="button"
              aria-current={isActive}
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  onSelectKind(kind);
                }
              }}
            >
              <Icon size={14} strokeWidth={1.75} style={{ flexShrink: 0 }} />
              <span className="sidebar-row-title">{label}</span>
              {(kind !== 'vault' || vaultUnlocked) && (
                <span className="sidebar-count">{count}</span>
              )}
            </div>
          );
        })}
      </div>

      <div className="sidebar-header">
        <span>Folders</span>
        <button
          type="button"
          className="sidebar-header-action"
          onClick={onNewFolder}
          title="New folder"
        >
          +
        </button>
        {onClose && (
          <button
            type="button"
            className="sidebar-header-action"
            onClick={onClose}
            title="Close sidebar"
            aria-label="Close sidebar"
          >
            <X size={15} strokeWidth={1.75} />
          </button>
        )}
      </div>

      <div className="ob-host ob-host-grow" ref={folderScroll.hostRef}>
        <div className="sidebar-scroll" role="tree" aria-label="Folders" ref={folderScroll.scrollerRef}>
          {tree.length === 0 ? (
            <div className="sidebar-empty">No folders yet.</div>
          ) : (
            tree.map((node) => (
              <FolderRow
                key={node.folder.id}
                node={node}
                depth={0}
                selectedId={selection.kind === 'notes' ? selection.folderId : null}
                collapsed={collapsed}
                onToggleCollapse={onToggleCollapse}
                onSelectFolder={onSelectFolder}
              />
            ))
          )}
        </div>
      </div>

      <div className="sidebar-footer">
        <div className="sidebar-footer-row">
          <button
            type="button"
            className="sidebar-icon-button"
            onClick={onSync}
            disabled={syncing}
            title={syncing ? 'Syncing…' : 'Sync now'}
            aria-label="Sync now"
          >
            <RefreshCw size={14} strokeWidth={1.75} className={syncing ? 'spin' : undefined} />
          </button>
          <button
            type="button"
            className="sidebar-icon-button"
            onClick={onOpenSettings}
            title="Cloud storage settings"
            aria-label="Cloud storage settings"
          >
            <Settings size={14} strokeWidth={1.75} />
          </button>
          <button
            type="button"
            className="sidebar-icon-button"
            onClick={onSignOut}
            title="Sign out"
            aria-label="Sign out"
          >
            <LogOut size={14} strokeWidth={1.75} />
          </button>
          {statusLabel && <span className="sidebar-footer-status">{statusLabel}</span>}
        </div>
      </div>
    </aside>
  );
}
